/**
 * useFileUpload - File Selection Hook (Modal-First Flow)
 * 
 * Handles file selection via drag-and-drop or file picker.
 * Does NOT upload immediately - instead signals parent to open upload modal.
 * 
 * Flow:
 * 1. User drops file or clicks file picker
 * 2. Hook validates file type and stores the file
 * 3. Calls onFileSelected callback with the file
 * 4. Parent opens ReferenceUploadModal with file attached
 * 5. Modal handles the actual upload after user fills metadata
 */

import { useState, useCallback, useRef } from 'react';

// Supported file extensions for reference documents
const SUPPORTED_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.txt', '.md'];

interface UseFileUploadProps {
  supportsUpload: boolean;
  onFileSelected?: (file: File) => void;  // Called when a valid file is selected
}

interface UseFileUploadReturn {
  // State
  selectedFile: File | null;
  isDraggingOver: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  validationError: string | null;
  
  // Drag handlers
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  
  // File handlers
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  
  // Actions
  clearSelectedFile: () => void;
}

/**
 * Validate file extension
 */
function isValidFileType(filename: string): boolean {
  const ext = '.' + filename.toLowerCase().split('.').pop();
  return SUPPORTED_EXTENSIONS.includes(ext);
}

export function useFileUpload({
  supportsUpload,
  onFileSelected,
}: UseFileUploadProps): UseFileUploadReturn {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Process a selected file - validate and notify parent
   */
  const processFile = useCallback((file: File) => {
    // Validate file type
    if (!isValidFileType(file.name)) {
      setValidationError('Supported formats: PDF, DOCX, XLSX, TXT, MD');
      return;
    }
    
    // Validate file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      setValidationError('File size must be less than 100MB');
      return;
    }
    
    // Clear any previous error
    setValidationError(null);
    
    // Store the file
    setSelectedFile(file);
    
    // Notify parent to open modal
    if (onFileSelected) {
      onFileSelected(file);
    }
  }, [onFileSelected]);

  /**
   * Handle drag over event
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (supportsUpload) {
      setIsDraggingOver(true);
    }
  }, [supportsUpload]);

  /**
   * Handle drag leave event
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
  }, []);

  /**
   * Handle drop event
   */
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
    
    if (!supportsUpload) return;
    
    const files = Array.from(e.dataTransfer.files);
    const firstFile = files[0];
    
    if (firstFile) {
      processFile(firstFile);
    }
  }, [supportsUpload, processFile]);

  /**
   * Handle file input change
   */
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFile]);

  /**
   * Clear selected file (called after modal closes)
   */
  const clearSelectedFile = useCallback(() => {
    setSelectedFile(null);
    setValidationError(null);
  }, []);

  return {
    selectedFile,
    isDraggingOver,
    fileInputRef,
    validationError,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInputChange,
    clearSelectedFile,
  };
}
